import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Card, Table, Button, Form } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { PostFormData, Post } from '../../../../utils/httpProvider';
import { LICHHOCTHUCHANH_API } from '../../../../utils/apiUrl';

const ImportScheduleWizardModal = ({
    show,
    onHide,
    contractId,
    donViData,
    nganhDaoTaoData,
    studentsData,
    tutorsData,
    refetchSchedule
}) => {
    const [wizardStep, setWizardStep] = useState(1);
    const [wizardFile, setWizardFile] = useState(null);
    const [wizardRecords, setWizardRecords] = useState([]);
    const [wizardFilter, setWizardFilter] = useState('all'); // 'all', 'warning', 'error'
    const [wizardActiveRow, setWizardActiveRow] = useState(null);
    const [showWizardEditModal, setShowWizardEditModal] = useState(false);
    const [isProcessingWizard, setIsProcessingWizard] = useState(false);
    const [processingMsgIndex, setProcessingMsgIndex] = useState(0);

    useEffect(() => {
        let timer;
        if (isProcessingWizard) {
            setProcessingMsgIndex(0);
            timer = setInterval(() => {
                setProcessingMsgIndex(prev => (prev + 1) % 4);
            }, 1800);
        }
        return () => clearInterval(timer);
    }, [isProcessingWizard]);

    const processingMessages = [
        { icon: "fas fa-file-excel", text: "Đang đọc cấu trúc các cột và tệp tin Excel..." },
        { icon: "fas fa-users-cog", text: "Đang phân tích danh sách học viên & đợt thực hành lâm sàng..." },
        { icon: "fas fa-user-md", text: "Đang đối chiếu Giảng viên hướng dẫn & lịch nghỉ theo quy định..." },
        { icon: "fas fa-project-diagram", text: "Đang khởi tạo bản đồ ma trận & phát hiện cảnh báo xung đột..." }
    ];

    const handleWizardFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setWizardFile(file);
            setWizardStep(2); // Auto proceed to step 2 (column mapping view)
        }
    };

    const handleWizardImport = async () => {
        if (!wizardFile) return;

        setIsProcessingWizard(true);
        const formData = new FormData();
        formData.append('hopDongChiTietId', contractId);
        formData.append('file', wizardFile);

        try {
            const data = await PostFormData(LICHHOCTHUCHANH_API.IMPORT, formData);
            const list = data?.$values || data || [];
            setWizardRecords(list);
            setWizardStep(3); // Proceed to Step 3
        } catch (err) {
            toast.error(err?.message || 'Có lỗi xảy ra trong quá trình phân tích file Excel.');
        } finally {
            setIsProcessingWizard(false);
        }
    };

    const handleWizardConfirm = async () => {
        const validRecords = (wizardRecords || []).filter(r => r.trangThai !== 3);
        if (validRecords.length === 0) {
            toast.warning("Không có dòng hợp lệ nào để thực hiện nhập.");
            return;
        }

        setIsProcessingWizard(true);
        try {
            await Post(`${LICHHOCTHUCHANH_API.CONFIRM}/${contractId}`, null, validRecords);
            setWizardStep(4); // Final success step
        } catch (err) {
            toast.error(err?.message || 'Không thể lưu lịch học thực hành vào cơ sở dữ liệu.');
        } finally {
            setIsProcessingWizard(false);
        }
    };

    return (
        <Modal show={show} onHide={() => { if (wizardStep !== 3 || !isProcessingWizard) onHide(); }} size="xl" centered scrollable>
            <Modal.Header closeButton={wizardStep !== 3 || !isProcessingWizard} className="border-bottom-0 pb-0">
                <Modal.Title className="font-weight-bold">Nhập lịch thực hành lâm sàng tự động</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                {/* STEPPER HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-4 px-4 py-3 bg-light rounded-lg border">
                    <div className={`d-flex align-items-center ${wizardStep >= 1 ? 'text-primary font-weight-bold' : 'text-muted'}`}>
                        <span className={`badge ${wizardStep > 1 ? 'bg-success' : wizardStep === 1 ? 'bg-primary' : 'bg-secondary'} text-white rounded-circle mr-2`} style={{ width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {wizardStep > 1 ? <i className="fas fa-check"></i> : '1'}
                        </span>
                        Tải file lên
                    </div>
                    <div className="flex-grow-1 mx-3 border-top" style={{ borderTopStyle: 'dashed' }}></div>
                    <div className={`d-flex align-items-center ${wizardStep >= 2 ? 'text-primary font-weight-bold' : 'text-muted'}`}>
                        <span className={`badge ${wizardStep > 2 ? 'bg-success' : wizardStep === 2 ? 'bg-primary' : 'bg-secondary'} text-white rounded-circle mr-2`} style={{ width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {wizardStep > 2 ? <i className="fas fa-check"></i> : '2'}
                        </span>
                        Ánh xạ cột
                    </div>
                    <div className="flex-grow-1 mx-3 border-top" style={{ borderTopStyle: 'dashed' }}></div>
                    <div className={`d-flex align-items-center ${wizardStep >= 3 ? 'text-primary font-weight-bold' : 'text-muted'}`}>
                        <span className={`badge ${wizardStep > 3 ? 'bg-success' : wizardStep === 3 ? 'bg-primary' : 'bg-secondary'} text-white rounded-circle mr-2`} style={{ width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {wizardStep > 3 ? <i className="fas fa-check"></i> : '3'}
                        </span>
                        Kiểm tra dữ liệu
                    </div>
                    <div className="flex-grow-1 mx-3 border-top" style={{ borderTopStyle: 'dashed' }}></div>
                    <div className={`d-flex align-items-center ${wizardStep >= 4 ? 'text-primary font-weight-bold' : 'text-muted'}`}>
                        <span className={`badge ${wizardStep === 4 ? 'bg-primary' : 'bg-secondary'} text-white rounded-circle mr-2`} style={{ width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            4
                        </span>
                        Xác nhận nhập
                    </div>
                </div>

                {/* STEP 1: TẢI FILE LÊN */}
                {wizardStep === 1 && (
                    <div className="text-center py-5 border rounded-lg bg-light d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '280px', borderStyle: 'dashed' }}>
                        <i className="fas fa-cloud-upload-alt fa-4x text-primary mb-4" style={{ opacity: 0.8 }}></i>
                        <h5 className="font-weight-bold text-dark mb-2">Tải file Excel lên</h5>
                        <p className="text-muted small mb-4 px-3" style={{ maxWidth: '460px' }}>
                            Định dạng hỗ trợ `.xlsx`, `.xls`. Lịch đào tạo luân chuyển đợt sẽ tự động được kiểm tra chéo và đối chiếu danh sách Roster của hệ thống.
                        </p>
                        <input
                            type="file"
                            id="wizard-file-input"
                            style={{ display: 'none' }}
                            accept=".xlsx, .xls"
                            onChange={handleWizardFileChange}
                        />
                        <Button variant="primary" style={{ borderRadius: '8px', padding: '10px 24px' }} onClick={() => document.getElementById('wizard-file-input').click()}>
                            <i className="fas fa-file-excel mr-2"></i>Chọn file từ máy tính
                        </Button>
                    </div>
                )}

                {/* STEP 2: ÁNH XẠ CỘT */}
                {wizardStep === 2 && (
                    <div>
                        {isProcessingWizard ? (
                            <div className="py-5 px-4 text-center my-3 bg-light rounded-lg border shadow-xs position-relative overflow-hidden" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
                                <style>{`
                                    @keyframes pulse-ring {
                                        0% { transform: scale(0.95); opacity: 0.8; }
                                        50% { transform: scale(1.18); opacity: 0.25; }
                                        100% { transform: scale(0.95); opacity: 0.8; }
                                    }
                                    @keyframes shimmer-bar {
                                        0% { background-position: -200% 0; }
                                        100% { background-position: 200% 0; }
                                    }
                                    .radar-container {
                                        position: relative;
                                        width: 100px;
                                        height: 100px;
                                        margin: 0 auto 24px auto;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    }
                                    .radar-ring {
                                        position: absolute;
                                        width: 100%;
                                        height: 100%;
                                        border-radius: 50%;
                                        background: rgba(13, 148, 136, 0.15);
                                        border: 2px solid rgba(13, 148, 136, 0.3);
                                        animation: pulse-ring 2s infinite ease-in-out;
                                    }
                                    .radar-core {
                                        position: relative;
                                        z-index: 2;
                                        width: 64px;
                                        height: 64px;
                                        border-radius: 50%;
                                        background: #ffffff;
                                        box-shadow: 0 6px 16px rgba(13, 148, 136, 0.2);
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    }
                                    .shimmer-progress {
                                        height: 8px;
                                        border-radius: 4px;
                                        background: linear-gradient(90deg, #0d9488 25%, #5eead4 50%, #0d9488 75%);
                                        background-size: 200% 100%;
                                        animation: shimmer-bar 1.5s infinite linear;
                                    }
                                `}</style>

                                <div className="radar-container">
                                    <div className="radar-ring"></div>
                                    <div className="radar-core">
                                        <i className="fas fa-file-excel fa-2x text-success"></i>
                                    </div>
                                </div>

                                <h5 className="font-weight-bold text-dark mb-2">Hệ thống đang phân tích & khớp dữ liệu Excel</h5>
                                <div className="mx-auto mb-4" style={{ maxWidth: '380px' }}>
                                    <div className="shimmer-progress"></div>
                                </div>

                                <div className="d-inline-flex align-items-center bg-white px-4 py-2 rounded-pill border shadow-xs">
                                    <i className={`${processingMessages[processingMsgIndex].icon} mr-2`} style={{ color: '#0d9488' }}></i>
                                    <span className="font-weight-bold small text-secondary">{processingMessages[processingMsgIndex].text}</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="alert alert-info small shadow-sm mb-4">
                                    <i className="fas fa-info-circle mr-2"></i>
                                    Hệ thống đã tự động khớp các cột trong Excel dựa theo mẫu biểu thời khóa biểu lâm sàng chuẩn (Mẫu Hướng B).
                                </div>
                                <h6 className="font-weight-bold text-dark mb-3">Thông tin tệp tin đã chọn:</h6>
                                <div className="p-3 bg-light rounded-lg border mb-4 d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-file-excel fa-2x text-success mr-3"></i>
                                        <div>
                                            <div className="font-weight-bold text-dark">{wizardFile?.name}</div>
                                            <div className="text-muted small">{(wizardFile?.size / 1024).toFixed(1)} KB</div>
                                        </div>
                                    </div>
                                    <Button variant="outline-danger" size="sm" onClick={() => { setWizardFile(null); setWizardStep(1); }} style={{ borderRadius: '6px' }}>
                                        Thay đổi file khác
                                    </Button>
                                </div>

                                <h6 className="font-weight-bold text-dark mb-3">Danh sách ánh xạ các cột:</h6>
                                <div className="table-responsive border rounded-lg bg-white mb-4">
                                    <Table hover className="mb-0" style={{ fontSize: '0.9rem', verticalAlign: 'middle' }}>
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Trường dữ liệu hệ thống</th>
                                                <th>Cột tương ứng trong Excel</th>
                                                <th>Kiểu dữ liệu</th>
                                                <th className="text-center">Trạng thái</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="font-weight-bold text-dark">Ngày bắt đầu đợt <span className="text-danger">*</span></td>
                                                <td>Cột A (Ngày bắt đầu)</td>
                                                <td>Ngày (Date)</td>
                                                <td className="text-center text-success"><i className="fas fa-check-circle"></i> Khớp tự động</td>
                                            </tr>
                                            <tr>
                                                <td className="font-weight-bold text-dark">Ngày kết thúc đợt <span className="text-danger">*</span></td>
                                                <td>Cột B (Ngày kết thúc)</td>
                                                <td>Ngày (Date)</td>
                                                <td className="text-center text-success"><i className="fas fa-check-circle"></i> Khớp tự động</td>
                                            </tr>
                                            <tr>
                                                <td className="font-weight-bold text-dark">Ngày nghỉ theo lịch</td>
                                                <td>Cột C (BSNT nghỉ theo lịch)</td>
                                                <td>Ngày (Date)</td>
                                                <td className="text-center text-success"><i className="fas fa-check-circle"></i> Khớp tự động</td>
                                            </tr>
                                            <tr>
                                                <td className="font-weight-bold text-dark">Học viên / Mã học viên <span className="text-danger">*</span></td>
                                                <td>Cột G (BSNT) & Cột H (ID)</td>
                                                <td>Chuỗi chữ (String)</td>
                                                <td className="text-center text-success"><i className="fas fa-check-circle"></i> Khớp tự động</td>
                                            </tr>
                                            <tr>
                                                <td className="font-weight-bold text-dark">Khoa / Đơn vị thực tập <span className="text-danger">*</span></td>
                                                <td>Cột I (Khoa/Trung tâm)</td>
                                                <td>Chuỗi chữ (String)</td>
                                                <td className="text-center text-success"><i className="fas fa-check-circle"></i> Khớp tự động</td>
                                            </tr>
                                            <tr>
                                                <td className="font-weight-bold text-dark">Giảng viên hướng dẫn (GVLS)</td>
                                                <td>Cột J (GVLS) & Cột K (ID Giảng viên)</td>
                                                <td>Danh sách chuỗi</td>
                                                <td className="text-center text-success"><i className="fas fa-check-circle"></i> Khớp tự động</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>

                                <div className="text-right">
                                    <Button variant="light" onClick={() => setWizardStep(1)} className="mr-2" style={{ borderRadius: '8px' }}>
                                        Quay lại
                                    </Button>
                                    <Button variant="primary" onClick={handleWizardImport} disabled={isProcessingWizard} style={{ borderRadius: '8px' }}>
                                        Tiếp tục phân tích <i className="fas fa-arrow-right ml-1"></i>
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* STEP 3: KIỂM TRA DỮ LIỆU */}
                {wizardStep === 3 && (() => {
                    const total = wizardRecords.length;
                    const valid = wizardRecords.filter(r => r.trangThai === 1 || !r.trangThai).length;
                    const warning = wizardRecords.filter(r => r.trangThai === 2).length;
                    const error = wizardRecords.filter(r => r.trangThai === 3).length;

                    const filtered = wizardRecords.filter(r => {
                        if (wizardFilter === 'error') return r.trangThai === 3;
                        if (wizardFilter === 'warning') return r.trangThai === 2;
                        return true;
                    });

                    return (
                        <div>
                            {/* SUMMARY CARDS */}
                            <Row className="mb-4 text-center">
                                <Col md={3}>
                                    <Card className="border shadow-sm h-100" style={{ borderRadius: '12px', borderLeft: '5px solid #6c757d' }}>
                                        <Card.Body className="p-3">
                                            <div className="text-muted small font-weight-bold text-uppercase">Tổng số dòng</div>
                                            <h2 className="font-weight-bold mb-0 mt-1 text-dark">{total}</h2>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="border shadow-sm h-100" style={{ borderRadius: '12px', borderLeft: '5px solid #28a745', backgroundColor: '#eefcf1' }}>
                                        <Card.Body className="p-3">
                                            <div className="text-success small font-weight-bold text-uppercase">Hợp lệ</div>
                                            <h2 className="font-weight-bold mb-0 mt-1 text-success">{valid}</h2>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="border shadow-sm h-100" style={{ borderRadius: '12px', borderLeft: '5px solid #ffc107', backgroundColor: '#fffdf0' }}>
                                        <Card.Body className="p-3">
                                            <div className="text-warning small font-weight-bold text-uppercase">Cảnh báo</div>
                                            <h2 className="font-weight-bold mb-0 mt-1 text-warning" style={{ color: '#d39e00' }}>{warning}</h2>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="border shadow-sm h-100" style={{ borderRadius: '12px', borderLeft: '5px solid #dc3545', backgroundColor: '#fff5f6' }}>
                                        <Card.Body className="p-3">
                                            <div className="text-danger small font-weight-bold text-uppercase">Lỗi</div>
                                            <h2 className="font-weight-bold mb-0 mt-1 text-danger">{error}</h2>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* FILTER TABS */}
                            <div className="d-flex mb-3">
                                <Button
                                    variant={wizardFilter === 'all' ? 'dark' : 'outline-dark'}
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => setWizardFilter('all')}
                                    style={{ borderRadius: '20px', padding: '6px 16px' }}
                                >
                                    Tất cả ({total})
                                </Button>
                                <Button
                                    variant={wizardFilter === 'error' ? 'danger' : 'outline-danger'}
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => setWizardFilter('error')}
                                    style={{ borderRadius: '20px', padding: '6px 16px' }}
                                >
                                    <i className="fas fa-times-circle mr-1"></i> Lỗi ({error})
                                </Button>
                                <Button
                                    variant={wizardFilter === 'warning' ? 'warning' : 'outline-warning'}
                                    size="sm"
                                    onClick={() => setWizardFilter('warning')}
                                    style={{ borderRadius: '20px', padding: '6px 16px' }}
                                >
                                    <i className="fas fa-exclamation-triangle mr-1"></i> Cảnh báo ({warning})
                                </Button>
                            </div>

                            {/* DATA LIST TABLE */}
                            <div className="table-responsive border rounded-lg bg-white mb-3" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                                <Table hover className="mb-0" style={{ fontSize: '0.85rem', verticalAlign: 'middle' }}>
                                    <thead className="bg-light sticky-top shadow-xs">
                                        <tr>
                                            <th className="text-center" style={{ width: '60px' }}>Dòng</th>
                                            <th style={{ width: '120px' }}>Bắt đầu</th>
                                            <th style={{ width: '120px' }}>Kết thúc</th>
                                            <th>Trình độ</th>
                                            <th>BSNT</th>
                                            <th>Khoa</th>
                                            <th>GVLS</th>
                                            <th>Trạng thái</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map((row, idx) => {
                                            const gvs = row.lichHocThucHanh_GiangViens || row.LichHocThucHanh_GiangViens || [];
                                            const gvDisplay = gvs.map(g => g.giangVienTen || g.GiangVienTen).join(', ') || '-';

                                            let rowStyle = {};
                                            if (row.trangThai === 3) rowStyle = { backgroundColor: '#fff5f6' };
                                            else if (row.trangThai === 2) rowStyle = { backgroundColor: '#fffdf0' };

                                            return (
                                                <tr key={row.id || idx} style={{ ...rowStyle, cursor: 'pointer' }} onClick={() => { setWizardActiveRow(row); setShowWizardEditModal(true); }} title="Click để chỉnh sửa dòng này">
                                                    <td className="text-center font-weight-bold text-muted">{idx + 1}</td>
                                                    <td>{row.ngayBatDau ? moment(row.ngayBatDau).format('DD/MM/YYYY') : <span className="text-danger">(trống)</span>}</td>
                                                    <td>{row.ngayKetThuc ? moment(row.ngayKetThuc).format('DD/MM/YYYY') : <span className="text-danger">(trống)</span>}</td>
                                                    <td>{row.trinhDo || '-'}</td>
                                                    <td className="font-weight-bold text-dark">{row.hocVienTen}</td>
                                                    <td>{row.donVi || <span className="text-muted small">Chưa khớp</span>}</td>
                                                    <td>{gvDisplay}</td>
                                                    <td>
                                                        {row.trangThai === 3 ? (
                                                            <span className="badge bg-danger text-white p-2" title={row.ghiChu}>
                                                                <i className="fas fa-times-circle mr-1"></i>{row.ghiChu || 'Lỗi'}
                                                            </span>
                                                        ) : row.trangThai === 2 ? (
                                                            <span className="badge bg-warning text-dark p-2" title={row.ghiChu}>
                                                                <i className="fas fa-exclamation-triangle mr-1"></i>{row.ghiChu || 'Cảnh báo'}
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-success text-white p-2">
                                                                <i className="fas fa-check mr-1"></i> Hợp lệ
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filtered.length === 0 && (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4 text-muted">Không có dữ liệu phù hợp bộ lọc.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="text-muted small mb-4">
                                <i className="fas fa-info-circle mr-1"></i> Hiển thị {filtered.length} trên {total} dòng. <strong>Nhấp một dòng lỗi hoặc cảnh báo để sửa trực tiếp trong bảng.</strong>
                            </div>

                            {/* FOOTER */}
                            <div className="d-flex justify-content-between align-items-center">
                                <Button variant="light" onClick={() => setWizardStep(2)} style={{ borderRadius: '8px' }}>
                                    Quay lại
                                </Button>
                                <div className="d-flex align-items-center">
                                    {error > 0 && (
                                        <span className="text-danger small font-weight-bold mr-3">
                                            <i className="fas fa-exclamation-circle mr-1"></i> Sửa {error} dòng lỗi trước khi tiếp tục
                                        </span>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={handleWizardConfirm}
                                        disabled={error > 0 || isProcessingWizard}
                                        style={{ borderRadius: '8px', padding: '10px 24px' }}
                                    >
                                        {isProcessingWizard ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                                Đang lưu lịch học...
                                            </>
                                        ) : (
                                            `Tiếp tục (${valid}/${total})`
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* STEP 4: XÁC NHẬN NHẬP */}
                {wizardStep === 4 && (() => {
                    const total = wizardRecords.length;
                    const valid = wizardRecords.filter(r => r.trangThai === 1 || !r.trangThai).length;
                    const warning = wizardRecords.filter(r => r.trangThai === 2).length;
                    const error = wizardRecords.filter(r => r.trangThai === 3).length;

                    const distribution = {};
                    wizardRecords.forEach(r => {
                        if (r.trangThai !== 3) {
                            const prog = r.chuyenNganh || r.ChuyenNganh || 'Tổng quát / Khác';
                            distribution[prog] = (distribution[prog] || 0) + 1;
                        }
                    });

                    const distItems = Object.entries(distribution).sort((a, b) => b[1] - a[1]);

                    return (
                        <div>
                            <div className="alert alert-success shadow-sm mb-4 d-flex align-items-center" style={{ borderRadius: '8px' }}>
                                <i className="fas fa-check-circle fa-2x text-success mr-3"></i>
                                <div>
                                    <h6 className="font-weight-bold mb-1">Đã nhập {valid} lịch đào tạo thành công!</h6>
                                    <div className="small text-muted">Từ file <strong>{wizardFile?.name}</strong> • hoàn tất lúc {moment().format('HH:mm, DD/MM/YYYY')}</div>
                                </div>
                            </div>

                            <Row className="mb-4 text-center">
                                <Col md={3}>
                                    <div className="border rounded-lg p-3 bg-light">
                                        <div className="text-muted small text-uppercase">Tổng số dòng</div>
                                        <h4 className="font-weight-bold mb-0 text-dark mt-1">{total}</h4>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="border rounded-lg p-3" style={{ backgroundColor: '#eefcf1' }}>
                                        <div className="text-success small text-uppercase">Nhập thành công</div>
                                        <h4 className="font-weight-bold mb-0 text-success mt-1">{valid}</h4>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="border rounded-lg p-3" style={{ backgroundColor: '#fffdf0' }}>
                                        <div className="text-warning small text-uppercase">Có cảnh báo</div>
                                        <h4 className="font-weight-bold mb-0 text-warning mt-1" style={{ color: '#d39e00' }}>{warning}</h4>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="border rounded-lg p-3" style={{ backgroundColor: '#fff5f6' }}>
                                        <div className="text-danger small text-uppercase">Thất bại (Không nhập)</div>
                                        <h4 className="font-weight-bold mb-0 text-danger mt-1">{error}</h4>
                                    </div>
                                </Col>
                            </Row>

                            {error > 0 && (
                                <div className="border border-danger rounded-lg p-3 mb-4 bg-white">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="font-weight-bold text-danger mb-0">
                                            <i className="fas fa-exclamation-triangle mr-2"></i>{error} dòng chưa nhập được
                                        </h6>
                                        <Button variant="outline-secondary" size="sm" style={{ borderRadius: '6px' }} onClick={() => {
                                            const csvContent = "data:text/csv;charset=utf-8,"
                                                + ["Dòng,BSNT,Lý do"].concat(wizardRecords.filter(r => r.trangThai === 3).map((r, i) => `${i + 1},"${r.hocVienTen}","${r.ghiChu}"`)).join("\n");
                                            const encodedUri = encodeURI(csvContent);
                                            const link = document.createElement("a");
                                            link.setAttribute("href", encodedUri);
                                            link.setAttribute("download", `Lich_Thuc_Hanh_Loi_${contractId}.csv`);
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}>
                                            <i className="fas fa-download mr-1"></i> Tải file lỗi
                                        </Button>
                                    </div>
                                    <div className="table-responsive border rounded bg-white">
                                        <Table className="mb-0" style={{ fontSize: '0.85rem' }}>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th style={{ width: '80px' }} className="text-center">Dòng</th>
                                                    <th>BSNT</th>
                                                    <th>Lý do</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {wizardRecords.filter(r => r.trangThai === 3).map((row, i) => (
                                                    <tr key={i}>
                                                        <td className="text-center text-muted">{i + 1}</td>
                                                        <td className="font-weight-bold text-dark">{row.hocVienTen}</td>
                                                        <td className="text-danger">{row.ghiChu}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            <h6 className="font-weight-bold text-dark mb-3">Phân bổ theo chương trình BSNT</h6>
                            <div className="p-3 bg-light rounded-lg border mb-4">
                                {distItems.map(([progName, count], idx) => {
                                    const percent = ((count / valid) * 100).toFixed(0);
                                    return (
                                        <div key={idx} className="mb-3">
                                            <div className="d-flex justify-content-between text-dark small font-weight-bold mb-1">
                                                <span>{progName}</span>
                                                <span>{count}</span>
                                            </div>
                                            <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                                                <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${percent}%`, borderRadius: '4px' }}></div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {distItems.length === 0 && (
                                    <div className="text-center text-muted small py-3">Không có thông tin phân bổ chương trình.</div>
                                )}
                            </div>

                            <div className="text-right">
                                <Button variant="light" onClick={() => { onHide(); refetchSchedule(); }} className="mr-2" style={{ borderRadius: '8px', padding: '8px 20px' }}>
                                    Đóng
                                </Button>
                                <Button variant="dark" onClick={() => { onHide(); refetchSchedule(); }} style={{ borderRadius: '8px', padding: '8px 20px' }}>
                                    <i className="fas fa-calendar-alt mr-1"></i> Xem lịch đã nhập
                                </Button>
                            </div>
                        </div>
                    );
                })()}
            </Modal.Body>

            {/* WIZARD ROW EDIT MODAL */}
            {wizardActiveRow && (
                <Modal show={showWizardEditModal} onHide={() => setShowWizardEditModal(false)} centered scrollable>
                    <Modal.Header closeButton className="border-bottom-0 pb-0">
                        <Modal.Title className="font-weight-bold">Sửa dòng lịch Excel</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-muted small font-weight-bold">Học viên <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="select"
                                    value={wizardActiveRow.hopDongChiTietHocVienId || ''}
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const link = (studentsData || []).find(s => (s.id || s.accountId) === selectedId);
                                        setWizardActiveRow({
                                            ...wizardActiveRow,
                                            hopDongChiTietHocVienId: selectedId,
                                            hocVienTen: link ? link.fullName : wizardActiveRow.hocVienTen
                                        });
                                    }}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <option value="">-- Chọn học viên --</option>
                                    {(studentsData || []).map(s => (
                                        <option key={s.id || s.accountId} value={s.id || s.accountId}>
                                            {s.fullName} ({s.pseudonym || s.identityCard || 'Không có mã'})
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted small font-weight-bold">Bắt đầu <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={wizardActiveRow.ngayBatDau ? moment(wizardActiveRow.ngayBatDau).format('YYYY-MM-DD') : ''}
                                            onChange={(e) => setWizardActiveRow({ ...wizardActiveRow, ngayBatDau: e.target.value })}
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-muted small font-weight-bold">Kết thúc <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={wizardActiveRow.ngayKetThuc ? moment(wizardActiveRow.ngayKetThuc).format('YYYY-MM-DD') : ''}
                                            onChange={(e) => setWizardActiveRow({ ...wizardActiveRow, ngayKetThuc: e.target.value })}
                                            style={{ borderRadius: '8px' }}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-muted small font-weight-bold">Khoa / Đơn vị thực tập</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={wizardActiveRow.donViId || ''}
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const direct = (donViData || []).find(d => d.id === selectedId);
                                        setWizardActiveRow({
                                            ...wizardActiveRow,
                                            donViId: selectedId,
                                            donVi: direct ? (direct.tenDonVi || direct.TenDonVi) : ''
                                        });
                                    }}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <option value="">-- Chọn khoa --</option>
                                    {(donViData || []).map(d => (
                                        <option key={d.id} value={d.id}>
                                            {d.tenDonVi || d.TenDonVi}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-muted small font-weight-bold">Chuyên ngành</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={wizardActiveRow.nganhDaoTaoId || ''}
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const direct = (nganhDaoTaoData || []).find(n => n.id === selectedId);
                                        setWizardActiveRow({
                                            ...wizardActiveRow,
                                            nganhDaoTaoId: selectedId,
                                            chuyenNganh: direct ? (direct.tenNganh || direct.TenNganh) : ''
                                        });
                                    }}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <option value="">-- Chọn chuyên ngành --</option>
                                    {(nganhDaoTaoData || []).map(n => (
                                        <option key={n.id} value={n.id}>
                                            {n.tenNganh || n.TenNganh}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-muted small font-weight-bold">Giảng viên hướng dẫn (GVLS)</Form.Label>
                                <div className="border p-3 rounded-lg bg-light" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                                    {(tutorsData || []).map(tutor => {
                                        const currentGvs = wizardActiveRow.lichHocThucHanh_GiangViens || wizardActiveRow.LichHocThucHanh_GiangViens || [];
                                        const isChecked = currentGvs.some(g => (g.hopDongChiTietGiangVienId || g.HopDongChiTietGiangVienId) === (tutor.id || tutor.accountId));
                                        return (
                                            <Form.Check
                                                key={tutor.id || tutor.accountId}
                                                type="checkbox"
                                                id={`wizard-tutor-chk-${tutor.id || tutor.accountId}`}
                                                label={tutor.fullName}
                                                checked={isChecked}
                                                onChange={(e) => {
                                                    let newList = [...currentGvs];
                                                    if (e.target.checked) {
                                                        newList.push({
                                                            hopDongChiTietGiangVienId: tutor.id || tutor.accountId,
                                                            giangVienTen: tutor.fullName,
                                                            laGiangVienChinh: newList.length === 0
                                                        });
                                                    } else {
                                                        newList = newList.filter(g => (g.hopDongChiTietGiangVienId || g.HopDongChiTietGiangVienId) !== (tutor.id || tutor.accountId));
                                                    }
                                                    if (newList.length > 0 && !newList.some(g => g.laGiangVienChinh || g.LaGiangVienChinh)) {
                                                        newList[0].laGiangVienChinh = true;
                                                    }
                                                    setWizardActiveRow({
                                                        ...wizardActiveRow,
                                                        lichHocThucHanh_GiangViens: newList,
                                                        LichHocThucHanh_GiangViens: newList
                                                    });
                                                }}
                                                className="mb-2"
                                            />
                                        );
                                    })}
                                </div>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-top-0 pt-0">
                        <Button variant="light" onClick={() => setShowWizardEditModal(false)} style={{ borderRadius: '8px' }}>
                            Hủy bỏ
                        </Button>
                        <Button variant="primary" style={{ borderRadius: '8px' }} onClick={() => {
                            const updatedList = wizardRecords.map(r => {
                                if (r.id === wizardActiveRow.id) {
                                    let newStatus = 1;
                                    let newMsg = '';
                                    if (!wizardActiveRow.hopDongChiTietHocVienId) {
                                        newStatus = 3;
                                        newMsg = 'Thiếu thông tin học viên';
                                    } else if (!wizardActiveRow.ngayBatDau || !wizardActiveRow.ngayKetThuc) {
                                        newStatus = 3;
                                        newMsg = 'Thiếu ngày bắt đầu hoặc ngày kết thúc';
                                    }
                                    return {
                                        ...wizardActiveRow,
                                        trangThai: newStatus,
                                        ghiChu: newMsg
                                    };
                                }
                                return r;
                            });
                            setWizardRecords(updatedList);
                            setShowWizardEditModal(false);
                            toast.success("Đã cập nhật dòng lịch học.");
                        }}>
                            Lưu dòng này
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Modal>
    );
};

export default ImportScheduleWizardModal;
